import React, { useState } from 'react';
import { Heading } from '../catalyst/heading';
import { Input } from '../catalyst/input';
import { Button } from '../catalyst/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../catalyst/table';
import { Text } from '../catalyst/text';
import { useUsers } from '../hooks/useUsers';
import ResourceLink from '../components/ResourceLink';

const PAGE_SIZE = 20;

const UsersPage: React.FC = () => {
	const [search, setSearch] = useState('');
	const [offset, setOffset] = useState(0);

	const { data: users, loading, error, count } = useUsers({
		search: search || undefined,
		limit: PAGE_SIZE,
		offset,
	});

	const totalPages = count != null ? Math.ceil(count / PAGE_SIZE) : 0;
	const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

	return (
		<div className="p-6">
			<div className="flex items-center justify-between">
				<Heading level={1}>Users</Heading>
			</div>

			<div className="mt-4 max-w-sm">
				<Input
					type="text"
					placeholder="Search by phone or name..."
					value={search}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
						setSearch(e.target.value);
						setOffset(0);
					}}
				/>
			</div>

			{error && (
				<div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
					{error}
				</div>
			)}

			{loading ? (
				<Text className="mt-6">Loading users...</Text>
			) : users.length === 0 ? (
				<Text className="mt-6">No users found.</Text>
			) : (
				<>
					<Table className="mt-6">
						<TableHead>
							<TableRow>
								<TableHeader>User ID</TableHeader>
								<TableHeader>Phone Number</TableHeader>
								<TableHeader>Display Name</TableHeader>
								<TableHeader>Email</TableHeader>
								<TableHeader>Created</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{users.map((user) => (
								<TableRow key={user.id} href={`/users/${user.id}`}>
									<TableCell>
										<ResourceLink id={user.id} />
									</TableCell>
									<TableCell>{user.phone_number}</TableCell>
									<TableCell>{user.display_name || '-'}</TableCell>
									<TableCell>{user.email || '-'}</TableCell>
									<TableCell>
										{new Date(user.created_at).toLocaleDateString()}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					{totalPages > 1 && (
						<div className="mt-4 flex items-center justify-between">
							<Text>
								Page {currentPage} of {totalPages}
								{count != null && ` (${count} users)`}
							</Text>
							<div className="flex gap-2">
								<Button
									plain
									disabled={offset === 0}
									onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
								>
									Previous
								</Button>
								<Button
									plain
									disabled={currentPage >= totalPages}
									onClick={() => setOffset(offset + PAGE_SIZE)}
								>
									Next
								</Button>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default UsersPage;
